"use client";

import React, { useMemo } from "react";
import { MultiStepLoader, type LoaderState } from "@/components/ui/multi-step-loader";

type ServiceLike = {
  title?: string;
  name?: string;
};

interface LoaderProps {
  loading?: boolean;
  services?: ServiceLike[];
  loadingStates?: LoaderState[];
  duration?: number;
  className?: string;
  mode?: "light" | "dark";
  closeable?: boolean;
  persistUntilClose?: boolean;
  overlayClassName?: string;
  fullscreen?: boolean;
  lockScrollWhenVisible?: boolean;
  onClose?: () => void;
}

const defaultStates: LoaderState[] = [
  { text: "Preparing services" },
  { text: "Loading service details" },
  { text: "Finalizing content" },
];

const Loader = ({
  loading = true,
  services = [],
  loadingStates,
  duration = 1500,
  className,
  mode = "light",
  closeable = false,
  persistUntilClose = false,
  overlayClassName,
  fullscreen = false,
  lockScrollWhenVisible = false,
  onClose,
}: LoaderProps) => {
  const derivedStates = useMemo<LoaderState[]>(() => {
    if (loadingStates && loadingStates.length > 0) {
      return loadingStates;
    }

    const fromServices = services
      .map((service) => service?.title || service?.name)
      .filter((text): text is string => Boolean(text?.trim()))
      .slice(0, 8)
      .map((text) => ({ text }));

    return fromServices.length > 0 ? fromServices : defaultStates;
  }, [loadingStates, services]);

  return (
    <MultiStepLoader
      loading={loading}
      loadingStates={derivedStates}
      duration={duration}
      className={className}
      textClassName={mode === "dark" ? "text-gray-300" : "text-gray-600"}
      indicatorClassName={mode === "dark" ? "border-[#8B31FF]" : "border-[#8B31FF]"}
      closeable={closeable}
      persistUntilClose={persistUntilClose}
      overlayClassName={overlayClassName}
      fullscreen={fullscreen}
      lockScrollWhenVisible={lockScrollWhenVisible}
      onClose={onClose}
    />
  );
};

export default Loader;
